(ns minorodata.components.transformations.groups
  (:require [clojure.set :as c.set]
            [lucide-react :refer [ArrowDownUp Clock Plus FileEdit]]
            [minorodata.app.handlers :refer [dispatch]]
            [minorodata.components.infinite-scroll.core :as c.infinite-scroll]
            [minorodata.components.spinner.core :as c.spinner]
            [re-posh.core :as re-posh]
            [reitit.frontend.easy :refer [href]]
            [ui.button :refer [button]]
            [ui.dropdown-menu :refer [dropdown-menu-content dropdown-menu-item
                                      dropdown-menu-root
                                      dropdown-menu-trigger]]
            [ui.tooltip :refer [tooltip]]
            [ui.typography :refer [h2 h3 small]]
            [ui.utils :refer [clsx]]
            [ui.date-time :refer [date-tooltip]]))


(defn fetch-more-groups []
  (let [page @(re-posh/subscribe [:groups-panel/prop :groups-panel/page])]
    (dispatch [:environments.api/fetch-groups {:page (inc page)}])))

(defn spinner-list-item []
  [:div {:class "flex justify-center py-4 px-5"}
   [c.spinner/spinner-lucide]])

(defn empty-groups-message []
  [:div {:class "px-6 py-5"}
   [:p "No groups found."]
   [:p "Try to use search or create the new one."]])

(defn groups-list [props & children]
  (into [:div (update props :class #(clsx "flex flex-col overflow-y-auto" %))]
        children))

(defn group-list-item [{:keys [group-id href on-click statuses active modification-states]}]
  (let [prod-env? @(re-posh/subscribe [:environments.subs/production-active?])

        group     @(re-posh/subscribe [:group.subs/panel-group group-id])
        tag       (if href :a :div)
        selected  (if (some? active) active (:selected? group))
        modified? (some #{"newly-created" "deleted" "directly-modified"} modification-states)

        {:keys [name updated-by updated-at scheduled?]} group]
    [tag {:href     href
          :data-cy  (str "group group-" group-id)
          :class    (clsx "px-5 py-3 hover:bg-kleene-hover-background transition-colors border-b group cursor-pointer"
                          {"bg-kleene-selected-background" selected})
          :on-click on-click}
     [h3 {:class "flex items-center gap-2"}
      (if prod-env?
        (when scheduled?
          [tooltip {:content "This group is scheduled"}
           [:> Clock
            {:size 16 :strokeWidth 2.5 :class "text-kleene-secondary-v2 shrink-0"}]])
        (when modified?
          [:> FileEdit {:size 16  :class "text-kleene-warning-02"}]))
      [:span {:class "shrink whitespace-nowrap text-ellipsis overflow-hidden"} name]]
     (when prod-env?
       [:div
        (if (pos? statuses)
          [small {:class "text-kleene-error-01"}
           (str statuses " failed")]
          [small {:class "text-kleene-text-secondary"}
           "No failures"])])
     [:div {:class "text-xs"}
      (when (and updated-at updated-by)
        [small "Updated " [date-tooltip {:date updated-at}] " by " updated-by])]]))

(def sort-options
  [{:id    :name
    :title "By name"}
   {:id    :name-desc
    :title "By name (DESC)"}
   {:id    :last-updated
    :title "By last updated"}
   {:id    :last-updated-desc
    :title "By last updated (DESC)"}
   {:id    :update-person
    :title "By person"}
   {:id    :update-person-desc
    :title "By person (DESC)"}])


(def sort-items-mapping
  {[:name :ASC]        :name
   [:name :DESC]       :name-desc
   [:updated_at :ASC]  :last-updated
   [:updated_at :DESC] :last-updated-desc
   [:updated_by :ASC]  :update-person
   [:updated_by :DESC] :update-person-desc})

(def sort-items-mapping-invert
  (c.set/map-invert sort-items-mapping))


(defn get-selected-item [{:groups-panel/keys [order-by order-dir]}]
  (get sort-items-mapping [order-by order-dir]))

(defn select-sort-option [option-id]
  (let [[order-by order-dir] (get sort-items-mapping-invert option-id)]
    (dispatch [:app.events/set-local-storage "groups-panel-sort" {:order-by order-by
                                                                  :order-dir order-dir}])
    (dispatch [:environments.api/fetch-groups {:page      1
                                               :order-by  order-by
                                               :order-dir order-dir
                                               :replace?  true}])))


(defn groups-sidebar []
  (dispatch [:connections.api/fetch-transform-statuses])

  (fn []
    (let [{:app-route/keys [name params]} @(re-posh/subscribe [:app-route/pull])
          groups     @(re-posh/subscribe [:environments.subs/get-groups])
          panel      @(re-posh/subscribe [:groups-panel/pull])
          loading?   (or @(re-posh/subscribe [:mold-http/loading? :group.api/fetch-groups])
                         @(re-posh/subscribe [:mold-http/loading? :environments.api/fetch-environment]))
          statuses   @(re-posh/subscribe [:transform-status-list/prop :transform-status-list/statuses])
          selected   (get-selected-item panel)
          prod-env?  @(re-posh/subscribe [:environments.subs/production-active?])
          modification-states @(re-posh/subscribe [:environments-panel/prop :environments-panel/modification-states])

          failed-statuses (->> statuses
                               (filter #(= (:status %) "failed"))
                               (group-by :group-id))]

      [:div {:class "border-r flex flex-col overflow-hidden"}
       [:header {:class "shrink-0 border-b px-5 py-4 flex justify-between items-center bg-kleene-background"}
        [h2 "Groups"]
        [:div {:class "flex gap-1"}
         [dropdown-menu-root
          [dropdown-menu-trigger
           [tooltip {:content "Sort groups"}
            [:div
             [button {:size      :icon-md
                      :variant   :secondary
                      :component :div
                      :data-cy   "groups-sort-btn"
                      :icon      ArrowDownUp}]]]]
          (into [dropdown-menu-content]
                (for [{:keys [id title]} sort-options]
                  [dropdown-menu-item {:on-click #(select-sort-option id)
                                       :data-cy  (str "group-sort-option-" id "-btn")
                                       :active   (= id selected)}
                   title]))]

         (when prod-env?
           [tooltip {:trigger-as-child false
                     :content "Add new group"}
            [button {:size     :icon-md
                     :variant  :secondary
                     :data-cy  "add-new-group-btn"
                     :icon     Plus
                     :href     (href name params {:create-group true})}]])]]

       [:div {:class "flex-1 overflow-hidden"}
        (if (and (= (count groups) 0)
                 (not loading?))
          [empty-groups-message]

          (into
           [c.infinite-scroll/main
            {:component       groups-list
             :loader          [spinner-list-item]
             :loading?        loading?
             :on-scroll-end   fetch-more-groups}]
           (for [{:group/keys [id]} groups]
             [group-list-item {:group-id id
                               :href     (href :transforms {:group-id id})
                               :statuses (count (get failed-statuses id))
                               :modification-states (get modification-states (keyword id))}])))]])))
